/**
 * PURPOSE: Modal liviano "Pruebas que suman en la clasificación" (ⓘ junto al título de la Clasificación de Atletismo).
 * RESPONSIBILITIES: Listar, por rama y nivel, las pruebas que entran en el cómputo con su estado (Oficial / En curso /
 *   Pendiente), etiquetar Postas y Combinadas, y mostrar el puntaje por puesto. TODO viene del servidor
 *   (`pruebasContempladas`, `puntajePorPuesto`: la misma función que calcula la clasificación). Se cierra con Esc, con la X o
 *   al hacer clic afuera (MUI Joy Modal, que además atrapa el foco y lo devuelve al ícono).
 * COLLABORATORS: StandingsView.jsx, standingsPruebas.js, ui.jsx (PruebaStatusChip).
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/PruebasQueSumanModal.jsx
 */
import React from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";

import { KIND_TAGS, groupPruebasContempladas, scoringLines } from "../standingsPruebas.js";
import { Badge, PruebaStatusChip } from "./ui.jsx";

const PruebasQueSumanModal = ({ open, onClose, pruebas, puntajePorPuesto }) => {
  const groups = groupPruebasContempladas(pruebas);
  const lines = scoringLines(puntajePorPuesto);
  return (
    <Modal open={Boolean(open)} onClose={onClose}>
      <ModalDialog
        aria-labelledby="ath-pruebas-suman-title"
        sx={{ width: "min(94vw, 560px)", maxHeight: "86vh", borderRadius: "16px", overflow: "auto", p: 2.5 }}
      >
        <ModalClose aria-label="Cerrar" />
        <DialogTitle id="ath-pruebas-suman-title">Pruebas que suman en la clasificación</DialogTitle>
        <DialogContent>
          {groups.length ? (
            <div className="space-y-4 pb-1">
              {groups.map((group) => (
                <section key={group.key} aria-label={group.label}>
                  <h4 className="text-sm font-black uppercase tracking-wide text-indigo-700">{group.label}</h4>
                  {group.levels.map((level) => (
                    <div key={level.key} className="mt-1.5">
                      {level.label ? <p className="text-xs font-bold text-slate-500">{level.label}</p> : null}
                      <ul className="mt-1 divide-y divide-slate-100 rounded-xl border border-slate-200">
                        {level.pruebas.map((prueba) => (
                          <li key={prueba.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                            <span className="min-w-0 text-sm font-semibold text-slate-800">
                              {prueba.name}
                              {KIND_TAGS[prueba.kind] ? (
                                <Badge tone="indigo" className="ml-2 align-middle">
                                  {KIND_TAGS[prueba.kind]}
                                </Badge>
                              ) : null}
                            </span>
                            <PruebaStatusChip status={prueba.status} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">El servidor no envió la lista de pruebas.</p>
          )}
          {lines.length ? (
            <div className="mt-4 rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-700">
              <p className="font-black uppercase tracking-wide text-slate-500">Puntaje por puesto</p>
              {lines.map((entry) => (
                <p key={entry.multiplier} className="mt-1 leading-5">
                  <span className="font-bold">{entry.title}:</span> {entry.line}
                </p>
              ))}
            </div>
          ) : null}
          <p className="mt-3 text-xs font-semibold text-slate-500">Las pruebas combinadas suman puntos como una prueba más.</p>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default PruebasQueSumanModal;
